import { z } from 'zod';
import {
  ActivityType,
  AssessmentStatus,
  CapacityBand,
  DuplicateStatus,
  FeedbackVerdict,
  HouseholdOrigin,
  HvBand,
  HvDecision,
  NarrativeSource,
  PipelineList,
  RecommendationSource,
  RecommendationType,
  Signal,
} from '../enums';
import { DuplicateLevel, DuplicateSignal } from '../duplicates';
import { EvidenceItem } from '../scoring';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');
const trimmed = (max: number) => z.string().trim().min(1).max(max);
const optionalTrimmed = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v ? v : null));

export const ApplicantSummary = z.object({
  id: z.string().uuid(),
  firstName: z.string(),
  gradeApplying: z.string().nullable(),
  hvDecision: HvDecision,
  admissionStatus: z.string().nullable(),
});
export type ApplicantSummary = z.infer<typeof ApplicantSummary>;

export const HouseholdSummary = z.object({
  id: z.string().uuid(),
  cycleId: z.string().uuid(),
  familyLastName: z.string(),
  parentNames: z.string().nullable(),
  origin: HouseholdOrigin,
  applicants: z.array(ApplicantSummary),
  recommendationTypes: z.array(RecommendationType),
  recommenders: z.array(z.string()),
  aiScore: z.number().int().nullable(),
  effectiveScore: z.number().int().nullable(),
  overridden: z.boolean(),
  band: HvBand,
  signals: z.array(Signal),
  assessmentStatus: z.enum(['succeeded', 'failed', 'pending', 'none']),
  decisions: z.array(HvDecision),
  capacityBand: CapacityBand,
  ownerUserId: z.string().uuid().nullable(),
  ownerName: z.string().nullable(),
  nextFollowUpAt: isoDate.nullable(),
  lastContactAt: z.string().nullable(),
  openDuplicates: z.number().int(),
  updatedAt: z.string(),
});
export type HouseholdSummary = z.infer<typeof HouseholdSummary>;

export const HouseholdListQuery = z.object({
  cycleId: z.string().uuid().optional(),
  list: PipelineList.default('all'),
  decisions: z
    .string()
    .optional()
    .transform((v) => (v ? v.split(',').filter(Boolean) : undefined))
    .pipe(z.array(HvDecision).optional()),
  band: z.enum(['all', 'high', 'medium', 'low', 'unscored']).default('all'),
  q: z.string().trim().max(200).optional(),
});
export type HouseholdListQuery = z.infer<typeof HouseholdListQuery>;

export const HouseholdListResponse = z.object({
  cycleId: z.string().uuid(),
  items: z.array(HouseholdSummary),
  counts: z.record(PipelineList, z.number().int()),
  thresholds: z.object({ high: z.number().int(), medium: z.number().int() }),
});
export type HouseholdListResponse = z.infer<typeof HouseholdListResponse>;

export const ApplicantDetail = ApplicantSummary.extend({
  veracrossPersonId: z.string().nullable(),
  veracrossApplicationId: z.string().nullable(),
  enrollmentLikelihood: z.string().nullable(),
  checklistCompleted: z.number().int().nullable(),
  checklistTotal: z.number().int().nullable(),
  veracrossUrl: z.string().nullable(),
  campusApplying: z.string().nullable(),
  admissionSource: z.string().nullable(),
  appliedPreviously: z.string().nullable(),
  enrollmentDecision: z.string().nullable(),
  enrollmentDecisionDate: z.string().nullable(),
  applicationDate: z.string().nullable(),
  inquiryDate: z.string().nullable(),
});
export type ApplicantDetail = z.infer<typeof ApplicantDetail>;

export const NarrativeDto = z.object({
  id: z.string().uuid(),
  applicantId: z.string().uuid(),
  applicantFirstName: z.string(),
  source: NarrativeSource,
  text: z.string(),
  receivedAt: z.string(),
});
export type NarrativeDto = z.infer<typeof NarrativeDto>;

export const RecommendationDto = z.object({
  id: z.string().uuid(),
  householdId: z.string().uuid(),
  type: RecommendationType,
  recommenderName: z.string(),
  recommenderUserId: z.string().uuid().nullable(),
  connection: z.string().nullable(),
  valueAdd: z.string().nullable(),
  familyDetails: z.string().nullable(),
  academicStanding: z.string().nullable(),
  disciplinaryStanding: z.string().nullable(),
  willEnroll: z.string().nullable(),
  additionalInfo: z.string().nullable(),
  submittedAt: z.string(),
  source: RecommendationSource,
});
export type RecommendationDto = z.infer<typeof RecommendationDto>;

export const AssessmentDto = z.object({
  id: z.string().uuid(),
  status: AssessmentStatus,
  aiScore: z.number().int().nullable(),
  signals: z.array(Signal),
  evidence: z.array(EvidenceItem),
  rationale: z.string().nullable(),
  confidence: z.number().nullable(),
  model: z.string(),
  promptVersion: z.string(),
  thresholdVersion: z.string(),
  failureReason: z.string().nullable(),
  scoredAt: z.string(),
});
export type AssessmentDto = z.infer<typeof AssessmentDto>;

export const OverrideDto = z.object({
  id: z.string().uuid(),
  assessmentId: z.string().uuid(),
  overrideScore: z.number().int(),
  reason: z.string(),
  byUserName: z.string(),
  at: z.string(),
});
export type OverrideDto = z.infer<typeof OverrideDto>;

export const FeedbackDto = z.object({
  id: z.string().uuid(),
  assessmentId: z.string().uuid(),
  verdict: FeedbackVerdict,
  comment: z.string().nullable(),
  byUserName: z.string(),
  at: z.string(),
});
export type FeedbackDto = z.infer<typeof FeedbackDto>;

export const AdvancementDto = z.object({
  capacityBand: CapacityBand,
  capacityRaw: z.string().nullable(),
  lifetimeGiving: z.number().nullable(),
  constituentId: z.string().nullable(),
  solicitor1: z.string().nullable(),
  solicitor2: z.string().nullable(),
  notes: z.string().nullable(),
});
export type AdvancementDto = z.infer<typeof AdvancementDto>;

export const ActivityDto = z.object({
  id: z.string().uuid(),
  type: ActivityType,
  text: z.string(),
  byUserName: z.string(),
  occurredAt: z.string(),
});
export type ActivityDto = z.infer<typeof ActivityDto>;

export const DuplicateDto = z.object({
  id: z.string().uuid(),
  householdId: z.string().uuid(),
  householdName: z.string(),
  candidateHouseholdId: z.string().uuid(),
  candidateHouseholdName: z.string(),
  reason: z.string(),
  status: DuplicateStatus,
  score: z.number().int().nullable(),
  level: DuplicateLevel.nullable(),
  signals: z.array(DuplicateSignal),
  referralId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DuplicateDto = z.infer<typeof DuplicateDto>;

export const HouseholdDetail = HouseholdSummary.extend({
  veracrossHouseholdId: z.string().nullable(),
  mergedFromProspectId: z.string().uuid().nullable(),
  applicants: z.array(ApplicantDetail),
  narratives: z.array(NarrativeDto),
  recommendations: z.array(RecommendationDto),
  assessment: AssessmentDto.nullable(),
  override: OverrideDto.nullable(),
  feedback: z.array(FeedbackDto),
  advancement: AdvancementDto,
  activities: z.array(ActivityDto),
  duplicates: z.array(DuplicateDto),
});
export type HouseholdDetail = z.infer<typeof HouseholdDetail>;

export const RecommendationInput = z.object({
  type: RecommendationType,
  recommenderName: trimmed(200),
  connection: optionalTrimmed(5000),
  valueAdd: optionalTrimmed(5000),
  familyDetails: optionalTrimmed(2000),
  academicStanding: optionalTrimmed(5000),
  disciplinaryStanding: optionalTrimmed(5000),
  willEnroll: optionalTrimmed(5000),
  additionalInfo: optionalTrimmed(5000),
});
export type RecommendationInput = z.infer<typeof RecommendationInput>;

export const CreateProspectInput = z.object({
  cycleId: z.string().uuid().optional(),
  familyLastName: trimmed(120),
  parentNames: optionalTrimmed(500),
  veracrossHouseholdId: optionalTrimmed(64),
  applicants: z
    .array(
      z.object({
        firstName: trimmed(120),
        gradeApplying: optionalTrimmed(32),
        veracrossPersonId: optionalTrimmed(64),
      }),
    )
    .min(1)
    .max(10),
  narrative: optionalTrimmed(20_000),
  recommendation: RecommendationInput.nullish(),
  capacityBand: CapacityBand.default('unknown'),
});
export type CreateProspectInput = z.infer<typeof CreateProspectInput>;

export const UpdateApplicantInput = z
  .object({
    hvDecision: HvDecision.optional(),
    enrollmentLikelihood: optionalTrimmed(2000).optional(),
  })
  .refine((v) => v.hvDecision !== undefined || v.enrollmentLikelihood !== undefined, {
    message: 'Nothing to update',
  });
export type UpdateApplicantInput = z.infer<typeof UpdateApplicantInput>;

export const SetOverrideInput = z.object({
  score: z.number().int().min(0).max(100),
  reason: trimmed(2000),
});
export type SetOverrideInput = z.infer<typeof SetOverrideInput>;

export const ClearOverrideInput = z
  .object({
    reason: optionalTrimmed(2000),
  })
  .default({ reason: null });
export type ClearOverrideInput = z.infer<typeof ClearOverrideInput>;

export const FeedbackInput = z.object({
  verdict: FeedbackVerdict,
  comment: optionalTrimmed(2000),
});
export type FeedbackInput = z.infer<typeof FeedbackInput>;

export const ActivityInput = z.object({
  type: ActivityType,
  text: trimmed(5000),
  occurredAt: z.string().datetime({ offset: true }).optional(),
});
export type ActivityInput = z.infer<typeof ActivityInput>;

export const AssignmentInput = z.object({
  ownerUserId: z.string().uuid().nullable(),
  nextFollowUpAt: isoDate.nullable(),
});
export type AssignmentInput = z.infer<typeof AssignmentInput>;

export const AdvancementInput = z.object({
  capacityBand: CapacityBand,
  notes: optionalTrimmed(5000),
  solicitor1: optionalTrimmed(200),
  solicitor2: optionalTrimmed(200),
});
export type AdvancementInput = z.infer<typeof AdvancementInput>;

export const IdResponse = z.object({ id: z.string().uuid() });
export type IdResponse = z.infer<typeof IdResponse>;
